import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';

export interface BrandKitSelectorProps {
  selectedBrandKitId?: string;
  onBrandKitChange: (brandKitId: string) => void;
}

export default function BrandKitSelector({ selectedBrandKitId, onBrandKitChange }: BrandKitSelectorProps): JSX.Element {
  const { brandKits, selectedBrandKit, isLoading, selectBrandKit } = usePlatformBrandKits();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeId = selectedBrandKitId ?? selectedBrandKit?.id;
  const activeKit = brandKits.find(kit => kit.id === activeId) ?? selectedBrandKit;
  const label = isLoading ? 'Loading…' : (activeKit?.name ?? 'Brand Kit');
  const canSelectBrandKit = brandKits.length > 1;

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  function handleSelect(id: string) {
    selectBrandKit(id);
    onBrandKitChange(id);
    setIsOpen(false);
  }

  if (!isLoading && brandKits.length === 0) {
    return <></>;
  }

  const isDisabled = isLoading || !canSelectBrandKit;

  const triggerButton = (
    <button
      data-testid="brand-kit-selector-trigger"
      disabled={isDisabled}
      onClick={() => setIsOpen(prev => !prev)}
      className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon name="IconPalette" size={16} stroke={1.75} className="shrink-0 text-slate-600" />
      <span className="min-w-0 flex-1 truncate text-left" title={label !== 'Loading…' ? label : undefined}>
        {label}
      </span>
      <Icon name="IconChevronDown" size={16} stroke={1.75} className="shrink-0 text-slate-500" />
    </button>
  );

  return (
    <div ref={containerRef} className="relative w-full">
      {!canSelectBrandKit && !isLoading ? (
        <TooltipV2
          tooltipId="brand-kit-single-tooltip"
          tooltipContent="You have just one brand kit added"
          containerClassName="w-full"
          tooltipElement={triggerButton}
        />
      ) : (
        triggerButton
      )}

      {isOpen && !isLoading && (
        <div
          data-testid="brand-kit-selector-dropdown"
          className="absolute left-0 top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-md"
        >
          {brandKits.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500">No brand kits found</p>
          ) : (
            brandKits.map(kit => (
              <button
                key={kit.id}
                data-testid={`brand-kit-option-${kit.id}`}
                onClick={() => handleSelect(kit.id)}
                className="relative flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-100"
              >
                {activeId === kit.id && (
                  <Icon name="IconCheck" size={14} className="absolute left-1 shrink-0 text-slate-700" />
                )}
                <span className="min-w-0 flex-1 truncate pl-4" title={kit.name}>
                  {kit.name}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
